import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import SkillCard from '../components/SkillCard';
import {
  FaReact,
  FaMicrosoft,
  FaCloud,
  FaDatabase,
  FaChartLine,
  FaRobot,
  FaDocker,
  FaGitAlt,
  FaGitlab,
  FaAws,
} from 'react-icons/fa';
import { IconType } from 'react-icons';
import { skillsAPI, Skill } from '../services/api';

interface SkillWithIcon {
  skill: string;
  icon?: IconType;
  description: string;
  technologies: string[];
  proficiency?: number;
  category?: string;
}

const Skills: React.FC = () => {
  const [skills, setSkills] = useState<SkillWithIcon[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadSkills = async () => {
      try {
        const response = await skillsAPI.getAll();
        const iconMap: Record<string, IconType> = {
          FaMicrosoft,
          FaReact,
          FaCloud,
          FaDatabase,
          FaChartLine,
          FaRobot,
          FaDocker,
          FaGitAlt,
          FaGitlab,
          FaAws,
        };

        const skillsWithIcons: SkillWithIcon[] = response.data.map((skill: Skill) => ({
          skill: skill.name,
          icon: iconMap[skill.icon] || FaCloud,
          description: skill.description,
          technologies: skill.technologies,
          proficiency: skill.proficiency,
          category: skill.category,
        }));

        setSkills(skillsWithIcons);
      } catch (error) {
        console.error('Error loading skills:', error);
        // Fallback to static data
        setSkills([
          {
            skill: '.NET & C#',
            icon: FaMicrosoft,
            description: 'Expert-level proficiency in .NET ecosystem including ASP.NET Core, Entity Framework, and Blazor',
            technologies: ['C#', 'ASP.NET Core', 'Entity Framework', 'Blazor', '.NET 8'],
          },
          {
            skill: 'React & Frontend',
            icon: FaReact,
            description: 'Building modern, responsive user interfaces with React, TypeScript, and Tailwind CSS',
            technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Redux', 'Next.js'],
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadSkills();
  }, []);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading skills...</div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <DynamicSection
        title="Skills & Technologies"
        subtitle="Comprehensive overview of my technical expertise"
        className="bg-white dark:bg-gray-900"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skills.map((skill, index) => (
            <SkillCard key={index} {...skill} />
          ))}
        </div>
      </DynamicSection>
    </div>
  );
};

export default Skills;
